import { NotifySuccess } from '@/helpers/core/notifications'
import { createService, deleteService, getAllServices, updateService } from '@/services/rc/repair-services.service'
import type { IServiceFilters } from '@/types/rc/filters'
import type { IServiceForm } from '@/types/rc/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import type { ComputedRef } from 'vue'

export const useServices = () => {
  const queryClient = useQueryClient()

  const getServicesQuery = (filters: IServiceFilters, enabled: ComputedRef<boolean>) =>
    useQuery({
      queryKey: ['services', filters],
      queryFn: async () => await getAllServices(filters),
      enabled: enabled,
    })

  const deleteServiceMutation = useMutation({
    mutationFn: async (id: number) => await deleteService(id),
    onSuccess: () => {
      NotifySuccess(`Service deleted successfully.`)
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const createServiceMutation = useMutation({
    mutationFn: async (form: IServiceForm) => await createService(form),
    onSuccess: () => {
      NotifySuccess(`Service created successfully.`)
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const updateServiceMutation = useMutation({
    mutationFn: async ({ serviceID, form }: { serviceID: number; form: IServiceForm }) =>
      await updateService(serviceID, form),
    onSuccess: () => {
      NotifySuccess('Successfully updated service.')
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
  })

  return {
    getServicesQuery,
    deleteServiceMutation,
    createServiceMutation,
    updateServiceMutation,
  }
}
